import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import { useAuth } from '../context/AuthContext';
import YouTubeOfficialIcon from '../components/common/YouTubeOfficialIcon';

const GOOGLE_PRIVACY_URL = 'https://policies.google.com/privacy';
const YOUTUBE_TERMS_URL = 'https://www.youtube.com/t/terms';
const YOUTUBE_API_TERMS_URL = 'https://developers.google.com/youtube/terms/api-services-terms-of-service';
const GOOGLE_SECURITY_PERMISSIONS_URL = 'https://security.google.com/settings/security/permissions';
const COMPANY_NAME = 'JMG Brands & Talents GmbH';
const COMPANY_EMAIL = 'contact@joined.media';
const COMPANY_ADDRESS_LINES = ['Kemperplatz 1', 'D-10785 Berlin', 'Germany'];
const TOOL_URL = 'https://tool.click.fan';

const YouTubeLinkIcon = () => <YouTubeOfficialIcon />;

const Section = ({ icon, title, children }) => (
    <Paper
        elevation={0}
        sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
        }}
    >
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="h6">{title}</Typography>
            </Stack>
            {children}
        </Stack>
    </Paper>
);

const PrivacyPolicy = () => {
    const { user } = useAuth();
    const backTarget = user ? '/dashboard' : '/login';
    const backLabel = user ? 'Back to dashboard' : 'Back to login';

    useEffect(() => {
        const root = document.getElementById('root');
        const previousRootOverflow = root?.style.overflow;
        if (root) root.style.overflow = 'auto';

        return () => {
            if (root) root.style.overflow = previousRootOverflow || '';
        };
    }, []);

    return (
        <Box
            sx={{
                height: '100dvh',
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                background: (theme) =>
                    theme.palette.mode === 'dark'
                        ? 'linear-gradient(180deg, rgba(255,0,0,0.08) 0%, rgba(18,18,18,1) 22%, rgba(18,18,18,1) 100%)'
                        : 'linear-gradient(180deg, rgba(255,0,0,0.07) 0%, rgba(255,255,255,1) 24%, rgba(248,250,252,1) 100%)',
                py: { xs: 4, sm: 6 },
            }}
        >
            <Container maxWidth="md">
                <Stack spacing={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            component={RouterLink}
                            to={backTarget}
                            startIcon={<ArrowBackIcon />}
                            variant="text"
                        >
                            {backLabel}
                        </Button>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                icon={<YouTubeOfficialIcon />}
                                label="YouTube API Services"
                                color="error"
                                variant="outlined"
                            />
                            <Chip icon={<PolicyOutlinedIcon />} label="Privacy Policy" variant="outlined" />
                        </Stack>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <Stack spacing={2}>
                            <Typography variant="overline" sx={{ letterSpacing: 1.2 }}>
                                JMG Admin Tool
                            </Typography>
                            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', sm: '2.6rem' }, lineHeight: 1.05 }}>
                                Privacy Policy
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                This tool uses YouTube API Services and Google sign-in to provide channel discovery,
                                evaluation, reporting, and related internal workflows.
                            </Typography>
                            <Alert severity="info" icon={<GavelOutlinedIcon fontSize="inherit" />}>
                                By using features powered by YouTube API Services, users are also subject to the{' '}
                                <Link
                                    href={YOUTUBE_TERMS_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <YouTubeLinkIcon />
                                    YouTube Terms of Service
                                </Link>
                                .
                            </Alert>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexWrap="wrap" useFlexGap>
                                <Button
                                    component="a"
                                    href={GOOGLE_PRIVACY_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    endIcon={<LaunchIcon />}
                                >
                                    Google Privacy Policy
                                </Button>
                                <Button
                                    component="a"
                                    href={GOOGLE_SECURITY_PERMISSIONS_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    endIcon={<LaunchIcon />}
                                >
                                    Google Security Settings
                                </Button>
                                <Button
                                    component="a"
                                    href={YOUTUBE_API_TERMS_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    startIcon={<YouTubeOfficialIcon />}
                                    endIcon={<LaunchIcon />}
                                >
                                    YouTube API Terms
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Section icon={<YouTubeOfficialIcon />} title="Use of YouTube API Services">
                        <Typography variant="body1" color="text.secondary">
                            This application uses YouTube API Services to retrieve and display YouTube-related data for
                            internal business workflows. Depending on the feature, this may include public channel
                            metadata, video metadata, and performance-related YouTube API Data used inside the tool.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            The tool only uses YouTube API Data to operate, improve, secure, and audit the features
                            offered inside this application. The data is not intended to be processed outside the scope
                            of the permitted product workflows.
                        </Typography>
                    </Section>

                    <Section icon={<LockOutlinedIcon />} title="Google Account and Privacy Information">
                        <Typography variant="body1" color="text.secondary">
                            Users authenticate to this tool with Google sign-in. When a user signs in, the application
                            may receive basic account information needed to identify the signed-in user and control
                            access to the tool.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Google&apos;s handling of personal information is described in the{' '}
                            <Link href={GOOGLE_PRIVACY_URL} target="_blank" rel="noreferrer">
                                Google Privacy Policy
                            </Link>
                            . If a user wants to review or revoke Google permissions connected to this tool, they can
                            do so through Google&apos;s{' '}
                            <Link href={GOOGLE_SECURITY_PERMISSIONS_URL} target="_blank" rel="noreferrer">
                                security settings page
                            </Link>
                            .
                        </Typography>
                    </Section>

                    <Section icon={<PolicyOutlinedIcon />} title="What We Access, Store, and Use">
                        <Typography variant="body1" color="text.secondary">
                            We may access, collect, store, and otherwise use information that is reasonably necessary to
                            operate the application and its YouTube-related features. This may include account and
                            profile information, access-control information, user-submitted content, search inputs,
                            campaign and workflow data, technical device and browser information, usage data, log data,
                            and public YouTube API Data.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Depending on the feature, YouTube API Data may include public channel metadata, public video
                            metadata, public statistics, links, thumbnails, and other information returned by YouTube
                            API Services for search, evaluation, reporting, benchmarking, preview, and audit-related
                            workflows inside the tool.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Information may be stored in application databases, logs, backups, browser storage, and
                            operational caches for continuity, security, debugging, fraud prevention, internal
                            reporting, contractual documentation, and compliance review, subject to applicable law and
                            operational necessity.
                        </Typography>
                    </Section>

                    <Section icon={<LockOutlinedIcon />} title="How We Use, Process, and Share Information">
                        <Typography variant="body1" color="text.secondary">
                            We use and process information to authenticate users, manage permissions, operate search and
                            reporting workflows, display and evaluate YouTube-related information, support campaign and
                            creator operations, secure the application, investigate issues, maintain audit trails, and
                            improve service reliability and internal operations.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Information may be shared with authorized internal personnel at Joined Media Group and its
                            relevant operating entities on a need-to-know basis for account management, campaign
                            execution, finance, operations, support, legal review, security, and compliance purposes.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Information may also be processed by external service providers and infrastructure partners
                            that support hosting, storage, authentication, database operations, communications,
                            embedded-content delivery, security, and technical maintenance. We do not sell YouTube API
                            Data. We disclose information externally only as needed to operate the service, comply with
                            law, protect rights and security, or fulfill a user-initiated workflow.
                        </Typography>
                    </Section>

                    <Section icon={<PolicyOutlinedIcon />} title="Cookies and Similar Technologies">
                        <Typography variant="body1" color="text.secondary">
                            The application stores, accesses, and collects information directly or indirectly on or
                            from users&apos; devices and browsers through cookies, local storage, session storage,
                            cached browser data, and similar technologies.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            These technologies may be used for authentication, session continuity, security, user
                            preferences, interface settings, language settings, layout state, performance, debugging,
                            and embedded third-party content. Third-party services integrated into the application may
                            also place, access, or recognize cookies or similar technologies on users&apos; devices or
                            browsers.
                        </Typography>
                    </Section>

                    <Section icon={<DeleteOutlineIcon />} title="Deletion and Revocation Policy">
                        <Typography variant="body1" color="text.secondary">
                            Users can revoke this application&apos;s access to their Google account at any time by using
                            Google&apos;s{' '}
                            <Link href={GOOGLE_SECURITY_PERMISSIONS_URL} target="_blank" rel="noreferrer">
                                security settings page
                            </Link>
                            . Revoking access prevents further access through the granted Google permissions.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            If a user wants JMG to delete locally stored application data associated with their access,
                            they should submit a deletion request through their JMG administrator or internal support
                            contact. Verified requests are handled as far as technically feasible and legally permitted.
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Certain records may need to be retained for fraud prevention, security, accounting,
                            contractual documentation, backup integrity, or other legal compliance reasons. Where full
                            deletion is not possible, access will be restricted and data will be retained only as long
                            as required for those purposes.
                        </Typography>
                    </Section>

                    <Section icon={<GavelOutlinedIcon />} title="External Policies and Terms">
                        <Stack spacing={1}>
                            <Typography variant="body1" color="text.secondary">
                                Relevant external policies and terms:
                            </Typography>
                            <Link
                                href={YOUTUBE_TERMS_URL}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <YouTubeLinkIcon />
                                YouTube Terms of Service
                            </Link>
                            <Link
                                href={YOUTUBE_API_TERMS_URL}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <YouTubeLinkIcon />
                                YouTube API Services Terms of Service
                            </Link>
                            <Link href={GOOGLE_PRIVACY_URL} target="_blank" rel="noreferrer">
                                Google Privacy Policy
                            </Link>
                            <Link href={GOOGLE_SECURITY_PERMISSIONS_URL} target="_blank" rel="noreferrer">
                                Google Security Settings and Connected App Permissions
                            </Link>
                        </Stack>
                    </Section>

                    <Section icon={<GavelOutlinedIcon />} title="Contact Information">
                        <Stack spacing={0.75}>
                            <Typography variant="body1" color="text.secondary">
                                If you have questions about this Privacy Policy or would like to submit a privacy or
                                data deletion request, please contact:
                            </Typography>
                            <Typography variant="body1">{COMPANY_NAME}</Typography>
                            {COMPANY_ADDRESS_LINES.map((line) => (
                                <Typography key={line} variant="body2" color="text.secondary">
                                    {line}
                                </Typography>
                            ))}
                            <Link href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</Link>
                            <Link href={TOOL_URL} target="_blank" rel="noreferrer">
                                {TOOL_URL}
                            </Link>
                        </Stack>
                    </Section>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, sm: 3 },
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1">Policy maintenance</Typography>
                            <Typography variant="body2" color="text.secondary">
                                This page is intended to provide a persistent in-product privacy reference for users of
                                the JMG Admin Tool. It should be updated whenever YouTube-related features, Google
                                account permissions, or deletion procedures materially change.
                            </Typography>
                            <Divider />
                            <Typography variant="caption" color="text.secondary">
                                Last updated: June 11, 2026
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;
